import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useCharityCauses } from "@/hooks/useCharityCauses";
import { useCreateDefaultCauseSet, useUpdateDefaultCauseSet } from "@/hooks/useDefaultCauses";
import type { DefaultCauseSet } from "@/interfaces/defaultCauses";
import { formatUsd } from "@/utils/number-format";

/** Today in the BUSINESS timezone — the day the server's dates are anchored to. A person in
 *  Chicago or Chennai must see the same "today" the rule is applied in, or a list keyed to "today"
 *  silently becomes tomorrow's. */
function todayEt(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
}

/** Judd's cap, mirrored from the API's MAX_CAUSES so the client is stopped first. */
const MAX_CAUSES = 6;

/**
 * "Set new cause list" — pick a start date and the charities that list contains.
 *
 * A PLAIN CHECKBOX LIST sorted by name, not the polaroid `CauseSelector` the client-facing drawer
 * and campaign builder use. Those exist to make a values-based choice feel considered, with a story
 * panel and a photo; this is an operator setting a quarter's configuration and wants to find a name
 * and tick it. Price is shown here for the opposite reason it is hidden there — the denomination
 * decides whether a closing balance can be spent to exactly zero, which is an operator's concern.
 *
 * Shares are not offered: the split is even by rule and derived server-side.
 */
export default function SetCauseListDialog({
  open,
  onClose,
  currentFrom,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  /** The list this one supersedes, if any — the new date has to fall after it. */
  currentFrom: string | null;
  /** When set, the dialog edits this list's charities instead of opening a new period. */
  editing?: DefaultCauseSet | null;
}) {
  const { data: causes = [], isLoading } = useCharityCauses();
  const create = useCreateDefaultCauseSet();
  const update = useUpdateDefaultCauseSet();
  const isEdit = editing != null;
  const busy = create.isPending || update.isPending;

  // Defaults to TODAY in Eastern rather than blank: the overwhelmingly common intent is "from now",
  // and an empty field invites picking a date in whichever zone the person is sitting in.
  const [from, setFrom] = useState(todayEt());
  const [note, setNote] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Editing starts from what the list already contains, so an operator adds or removes rather than
  // rebuilding the selection from nothing.
  useEffect(() => {
    if (!open) return;
    setPicked(editing ? editing.causes.map((c) => c.causeProductId) : []);
    setFrom(editing ? editing.effectiveFrom : todayEt());
    setNote(editing?.note ?? "");
    setError(null);
  }, [open, editing]);

  // By NAME, as an operator scans it. The catalogue endpoint sorts by price for the client-facing
  // selector, which is the wrong order for finding a charity you already have in mind.
  const sorted = useMemo(
    () =>
      [...causes].sort((a, b) =>
        `${a.organization ?? ""} ${a.title}`.localeCompare(`${b.organization ?? ""} ${b.title}`),
      ),
    [causes],
  );

  function toggle(id: string) {
    setPicked((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX_CAUSES
          ? prev
          : [...prev, id],
    );
  }

  function handleClose() {
    if (busy) return;
    setFrom(todayEt());
    setNote("");
    setPicked([]);
    setError(null);
    create.reset();
    update.reset();
    onClose();
  }

  async function handleSave() {
    setError(null);
    if (!from) return setError("Choose the date this list starts.");
    if (picked.length === 0) return setError("Select at least one cause.");
    if (currentFrom && from <= currentFrom.slice(0, 10)) {
      return setError(
        `The new list must start after the current one (${currentFrom.slice(0, 10)}).`,
      );
    }
    try {
      await create.mutateAsync({
        effectiveFrom: from,
        causeProductIds: picked,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the new cause list.");
    }
  }

  const sharePc = picked.length > 0 ? Math.floor(100 / picked.length) : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 22 }}>
        {isEdit ? "Edit cause list" : "Set new cause list"}
      </DialogTitle>
      <DialogContent dividers>
        {/* The single most misread thing about this screen. */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {isEdit
            ? "Changes apply to credits purchased from now on. Credits already bought keep the causes they were sold with — nothing is redirected."
            : "Applies to credits purchased from this date onward. Credits already bought keep the causes they were sold with — nothing is redirected."}
        </Alert>

        <TextField
          label="Starts on"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          fullWidth
          size="small"
          // A period's dates are fixed once it exists: moving them rewrites when the list applied,
          // which for a live list contradicts the credits already minted under it. A wrong date on
          // a scheduled list is fixed by deleting it.
          disabled={isEdit}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText={
            isEdit
              ? "A list's dates cannot be changed. Delete it and set a new one if the date is wrong."
              : from > todayEt()
                ? `Starts ${from} (Eastern). Until then the current list — or the built-in default — keeps applying.`
                : currentFrom
                  ? `Takes effect immediately. Must be after the current list (${currentFrom}).`
                  : "Takes effect immediately. Dates are Eastern time."
          }
        />

        <TextField
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          size="small"
          placeholder="Q1 2027"
          disabled={isEdit}
          sx={{ mt: 2 }}
        />

        <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 3, mb: 1 }}>
          Causes {picked.length > 0 && `— ${picked.length} selected, ${sharePc}% each`}
        </Typography>

        {isLoading && <Typography sx={{ fontSize: 13 }}>Loading causes…</Typography>}

        <Box
          sx={{
            maxHeight: 320,
            overflowY: "auto",
            border: "1px solid var(--color-card-border)",
            borderRadius: 1,
          }}
        >
          {sorted.map((c) => {
            const on = picked.includes(c.causeProductId);
            return (
              <Box
                key={c.causeProductId}
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  cursor: "pointer",
                  borderBottom: "1px solid var(--color-smoke)",
                  "&:last-of-type": { borderBottom: "none" },
                  bgcolor: on ? "var(--color-clear-dust)" : "transparent",
                }}
              >
                <Checkbox
                  size="small"
                  checked={on}
                  onChange={() => toggle(c.causeProductId)}
                  // The cap is a rule, not a hint: past it, unticked rows stop responding.
                  disabled={!on && picked.length >= MAX_CAUSES}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{c.organization}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
                    {c.title}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                  {formatUsd(c.price)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {picked.length >= MAX_CAUSES && (
          <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: 1 }}>
            Maximum of {MAX_CAUSES} causes.
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={busy}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          {busy ? "Saving…" : isEdit ? "Save changes" : "Save cause list"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
